import _get from "lodash/get";
import _set from "lodash/set";

import { FormDocument } from "prismicio-types";
import { useGetCustomisation } from "queries/legacy/product-queries";

import { useInterpolate } from "./prismic-utils";
import { useFindByKey } from "./schema-utils";

/**
 * All our default values need to be set at the `useForm` level for consistency and availability.
 *
 * So we need to take the FormDocument(s for creation flow) and check if we have a value in the URL
 * or a default value to set.
 */
export function useGetDefaultValuesFromFormDocuments(
  formDocuments: FormDocument[]
) {
  const findByKey = useFindByKey();
  const { data: customisation } = useGetCustomisation();
  const interpolate = useInterpolate();

  const defaultValues: Record<string, any> = {};
  const schemaKeys: string[] = [];

  /**
   * Gather the schema keys
   *
   * Some might be comma separated like `dob_year,dob_month,dob_day`
   */
  formDocuments.forEach((formDocument) =>
    formDocument.data.slices.forEach((slice) => {
      if ("muse_schema_key" in slice.primary && slice.primary.muse_schema_key) {
        if (slice.primary.muse_schema_key.indexOf(",") !== -1) {
          schemaKeys.push(...slice.primary.muse_schema_key.split(","));
        } else {
          schemaKeys.push(slice.primary.muse_schema_key);
        }
      }

      // DateInputField also optionally registers a "years count" schema key
      if (
        "years_count_muse_schema_key" in slice.primary &&
        slice.primary.years_count_muse_schema_key
      ) {
        schemaKeys.push(slice.primary.years_count_muse_schema_key);
      }
    })
  );

  /**
   * Map over all the keys to set the correct default values
   */
  schemaKeys.forEach((key) => {
    // The name of this function is the same of a proprietaty testing library
    // function so we we need to disable the eslint rule
    // eslint-disable-next-line testing-library/await-async-query
    const field = findByKey(key);

    if (!field) {
      return;
    }

    if (_get(customisation, field.urlKey, false) !== false) {
      // Always use the value in the URL if available
      _set(defaultValues, field.urlKey, _get(customisation, field.urlKey));
    } else if (
      "flags" in field &&
      field.flags &&
      field.flags.presence !== "required"
    ) {
      // Only set a default on optional fields
      // There are a few different ways that defaults are provided, so just try each in turn;
      if ("default" in field.flags && field.flags.default) {
        _set(defaultValues, field.urlKey, interpolate(field.flags.default));
      } else if ("examples" in field && field.examples?.[0]) {
        _set(defaultValues, field.urlKey, interpolate(field.examples[0]));
      }
    } else if (
      !("flags" in field) &&
      "examples" in field &&
      field.examples?.[0]
    ) {
      _set(defaultValues, field.urlKey, interpolate(field.examples[0]));
    }
  });

  /**
   * Now check if this form has Avatars to default the `type`s
   *
   * This is necessary because products don't like being sent other defaulted fields
   * (like `extras: 'none'`) without also being provided the context of the Avatar type.
   */
  //
  const avatarFields = schemaKeys.filter((key) => key.startsWith("avatars."));
  if (avatarFields.length > 0) {
    const roleTypes = new Set<string>();

    avatarFields.forEach((field) => {
      // Use the `schemaKeys` to get a unique Set of Avatar roles seen in these forms
      const [, role] = field.split(".");
      roleTypes.add(`avatars.${role}.type`);
    });

    roleTypes.forEach((roleType) => {
      // Now find each Avatar by role. This will return the parent `type` field.
      // See the inner workings of `findByKey` for more clarity here.
      // The name of this function is the same of a proprietaty testing library
      // function so we we need to disable the eslint rule
      // eslint-disable-next-line testing-library/await-async-query
      const field = findByKey(roleType);

      if (field && "allow" in field && Array.isArray(field.allow)) {
        _set(defaultValues, field.urlKey, field.allow[0]);
      }
    });
  }

  return defaultValues;
}
